import { CognitoIdToken } from "./endpoints/users/authTypes";
import z from "zod";

const createErrorSchema = <T extends ZodUnionArray>(errorTypes: T) =>
  z.object({ code: z.union(errorTypes), message: z.string() });

type ZodUnionArray = readonly [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]];

// rome-ignore lint/suspicious/noExplicitAny: <explanation>
type _Runtime = any;

export interface SafeResponse<TData, TErrorType extends string> {
  match<TMapperSuccess, TMappedBusinessError, TMappedUnexpectedError, TMappedUnauthorizedError>(matcher: {
    success: (data: TData) => TMapperSuccess;
    businessMatcher: { [key in TErrorType]: (error: { code: TErrorType; message: string }) => TMappedBusinessError };
    unexpected: (error: Error) => TMappedUnexpectedError;
    unauthorized: () => TMappedUnexpectedError;
  }): TMapperSuccess | TMappedBusinessError | TMappedUnexpectedError | TMappedUnauthorizedError;
}

export type FetcherArgs<TDataSchema extends z.ZodTypeAny, TErrorTypes extends ZodUnionArray> = {
  cognitoIdToken?: CognitoIdToken;
  endpoint: string;
  method: "GET" | "POST" | "PUT";
  data?: _Runtime;
  schema?: TDataSchema;
  errors?: TErrorTypes;
};

export type Fetcher = <TDataSchema extends z.ZodTypeAny, TErrorTypes extends ZodUnionArray>(
  args: FetcherArgs<TDataSchema, TErrorTypes>,
) => Promise<SafeResponse<z.infer<TDataSchema>, z.infer<z.ZodUnion<TErrorTypes>>>>;

export const createFetcher =
  (baseUrl: string): Fetcher =>
  async <TDataSchema extends z.ZodTypeAny, TErrorTypes extends ZodUnionArray>({
    endpoint,
    method,
    data: inputData,
    cognitoIdToken,
    schema,
    errors: errorTypes,
  }: FetcherArgs<TDataSchema, TErrorTypes>) => {
    const methodAcceptsBody = typeof method === "string" && ["POST", "PUT"].includes(method);
    const body = methodAcceptsBody && typeof inputData !== "undefined" ? JSON.stringify(inputData) : undefined;

    try {
      const response = await fetch(`${baseUrl}/${endpoint}`, {
        body,
        method,
        headers: new Headers({
          Accept: "application/json",
          ...(cognitoIdToken ? { Authorization: `Bearer ${cognitoIdToken}` } : {}),
        }),
      });

      if (response.status === 401) {
        return {
          match: (matcher) => matcher.unauthorized(),
        };
      }

      if (response.status >= 500) {
        return {
          match: (matcher) => matcher.unexpected(new Error(response.statusText)),
        };
      }

      const outputData = await response.json();

      if (errorTypes && "code" in outputData && outputData.code !== "unknown") {
        const errorsSchema = createErrorSchema(errorTypes);

        // Because wrapping and passing `errorTypes` as generic parameter somehow break types we have to cast it here
        const error = errorsSchema.parse(outputData) as unknown as {
          code: z.infer<z.ZodUnion<TErrorTypes>>;
          message: string;
        };

        return {
          match: (matcher) => matcher.businessMatcher[error.code](error),
        };
      }

      if (!response.ok) {
        const e =
          "message" in outputData
            ? new Error(outputData.message)
            : new Error(`${response.statusText} | ${JSON.stringify(outputData)}`);

        return {
          match: (matcher) => matcher.unexpected(e),
        };
      }

      return {
        match: (matcher) => matcher.success((schema ?? z.any()).parse(outputData)),
      };
    } catch (e) {
      return {
        match: (matcher) => matcher.unexpected(e as Error),
      };
    }
  };
